import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import './Forms.css';

interface PersonalDetails {
  name: string;
  email: string;
  phone: string;
  location: string;
  linkedin?: string;
  github?: string;
  portfolio?: string;
  targetPosition?: string;
}

const PersonalDetailsForm: React.FC = () => {
  const navigate = useNavigate();
  const { currentJourney, updateJourneyData } = useJourney();
  
  const [formData, setFormData] = useState<PersonalDetails>({
    name: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    github: '',
    portfolio: '',
    targetPosition: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentJourney?.collectedData?.personal) {
      setFormData({
        name: currentJourney.collectedData.personal.name || '',
        email: currentJourney.collectedData.personal.email || '',
        phone: currentJourney.collectedData.personal.phone || '',
        location: currentJourney.collectedData.personal.location || '',
        linkedin: currentJourney.collectedData.personal.linkedin || '',
        github: currentJourney.collectedData.personal.github || '',
        portfolio: currentJourney.collectedData.personal.portfolio || '',
        targetPosition: currentJourney.collectedData.personal.targetPosition || '',
      });
    }
  }, [currentJourney]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof PersonalDetails, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await updateJourneyData({
        personal: formData,
      });
      
      // Move to next stage (Education)
      navigate('/journey/forms/education');
    } catch (error) {
      console.error('Failed to save personal details:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="stage-indicator">
          <span className="stage-number">Stage 1 of 5</span>
          <div className="progress-bar-thin">
            <div className="progress-fill-thin" style={{ width: '20%' }} />
          </div>
        </div>
        <h1>Personal Information</h1>
        <p className="form-subtitle">Let's start with your basic contact details</p>
      </div>

      <form onSubmit={handleSubmit} className="resume-form">
        <div className="form-section">
          <h2>Contact Information</h2>
          
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="name">Full Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="John Doe"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
          </div>

          <div className="form-row two-columns">
            <div className="form-field">
              <label htmlFor="email">Email Address *</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john.doe@example.com"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone Number *</label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="location">Location *</label>
              <input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="San Francisco, CA"
                className={errors.location ? 'error' : ''}
              />
              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Online Presence <span className="optional">(Optional)</span></h2>
          
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="linkedin">LinkedIn Username</label>
              <input
                id="linkedin"
                type="text"
                value={formData.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="johndoe"
              />
              <small className="field-hint">Just your username, we'll build the full URL</small>
            </div>
          </div>

          <div className="form-row two-columns">
            <div className="form-field">
              <label htmlFor="github">GitHub Username</label>
              <input
                id="github"
                type="text"
                value={formData.github}
                onChange={(e) => handleChange('github', e.target.value)}
                placeholder="johndoe"
              />
              <small className="field-hint">Just your username</small>
            </div>

            <div className="form-field">
              <label htmlFor="portfolio">Personal Website</label>
              <input
                id="portfolio"
                type="text"
                value={formData.portfolio}
                onChange={(e) => handleChange('portfolio', e.target.value)}
                placeholder="https://johndoe.com"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Target Role <span className="optional">(Optional)</span></h2>
          
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="targetPosition">What position are you targeting?</label>
              <input
                id="targetPosition"
                type="text"
                value={formData.targetPosition}
                onChange={(e) => handleChange('targetPosition', e.target.value)}
                placeholder="Senior Software Engineer"
              />
              <small className="field-hint">This helps tailor your resume to the right role</small>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : 'Continue to Education →'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PersonalDetailsForm;

